import { useMemo } from "react";
import { z } from "zod";

import RadialBarChartView from "@/components/ui/radial-bar-chart/RadialBarChart";
import { buildRadialBarChartCatalogModel } from "@/components/ui/radial-bar-chart/radialBarChartUtils";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";

export const ChatRadialBarChartSpec = {
  props: z.object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    nameKey: z.string(),
    valueKey: z.string(),
    height: z.number().nullable().optional(),
    colors: z.record(z.string(), chartHexColorSchema).nullable().optional(),
  }),
  description:
    "Radial/circular bar chart for comparing values as concentric arcs. Good for Share of Voice, scores, gauges, and progress. Props: title (string or null), data (array of records), nameKey (segment label field), valueKey (numeric measure field), height (px or null for auto), colors (optional map of segment nameKey value → #RGB or #RRGGBB hex for arc fills and legend swatches). When colors is omitted, items whose labels contain positive, negative, or neutral (case-insensitive) get semantic sentiment CSS variables; other labels use var(--chart-1) through var(--chart-5) by index. Bind read-only tool data with $state paths on props when needed.",
  example: {
    title: "Share of Voice",
    data: [
      { feed: "Brand A", share: 42 },
      { feed: "Brand B", share: 28 },
      { feed: "Brand C", share: 18 },
      { feed: "Brand D", share: 12 },
    ],
    nameKey: "feed",
    valueKey: "share",
    height: 280,
  },
} as const;

/** Parses props and renders {@link RadialBarChartView}. */
export const ChatRadialBarChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const model = useMemo(() => {
    const parsed = ChatRadialBarChartSpec.props.safeParse(props);
    if (!parsed.success) return null;

    const { title, data, nameKey, valueKey, height, colors } = parsed.data;

    return {
      title: title ?? null,
      nameKey,
      valueKey,
      height: height ?? null,
      ...buildRadialBarChartCatalogModel({
        data,
        nameKey,
        valueKey,
        colors: colors ?? null,
      }),
    };
  }, [props]);

  if (model == null) return null;

  return (
    <RadialBarChartView
      title={model.title}
      data={model.chartData}
      chartConfig={model.chartConfig}
      dataKey={model.valueKey}
      nameKey={model.nameKey}
      height={model.height}
    />
  );
};
